/**
 * Configuration state management for the client app.
 * Exposes Svelte stores that hold the server's public configuration
 * and a loader function to fetch it from the API.
 */

import { writable, derived } from 'svelte/store';
import { getConfiguration } from './AppNetwork';
import type { Configuration } from './AppTypes';

/** Cached server configuration. `null` until the first successful fetch. */
export const configurationStore = writable<Configuration | null>(null);

/** Error message from the most recent configuration fetch attempt, if any. */
export const configurationError = writable<string | null>(null);

/** Whether the configuration has been successfully loaded. */
export const isConfigurationLoaded = derived(configurationStore, ($config) => $config !== null);

/** Fetches the server's public configuration and populates configurationStore. */
export async function loadConfiguration(): Promise<void> {
	try {
		const config = await getConfiguration();
		configurationStore.set(config);
		configurationError.set(null);
	} catch (error) {
		configurationError.set(error instanceof Error ? error.message : 'Failed to load configuration');
	}
}
